/**
 * API client de configuração global de gamificação.
 *
 * Integração com /v1/Configuration/gamification-config:
 * - Leitura: GET (pode retornar 204 sem corpo ou 200 com JSON)
 * - Gravação: POST com o body de configuração
 */

import type {
  GamificationConfigResponse,
  GamificationConfigUpdateRequest,
} from '@e-learning/shared';

export type {
  GamificationConfigResponse,
  GamificationConfigUpdateRequest,
} from '@e-learning/shared';

/** Cliente mínimo necessário para as chamadas (get/post). Compatível com ApiClient do core. */
export interface GamificationConfigApiClient {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown): Promise<T>;
}

const GAMIFICATION_CONFIG_PATH = 'v1/Configuration/gamification-config';

function isEmptyGamificationResponse(raw: unknown): boolean {
  if (raw === undefined || raw === null) return true;
  if (raw === '') return true;
  if (typeof raw === 'string' && raw.trim() === '') return true;
  return false;
}

function emptyGamificationConfig(): GamificationConfigResponse {
  return { pointsValidityDays: null };
}

function parseGamificationConfigRaw(raw: unknown): GamificationConfigResponse {
  const asAny = raw as unknown;

  const obj =
    typeof asAny === 'string'
      ? (() => {
          try {
            return JSON.parse(asAny) as unknown;
          } catch {
            return null;
          }
        })()
      : asAny;

  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid gamification-config response');
  }

  const record = obj as Record<string, unknown>;
  const rawDays = record.pointsValidityDays;

  let pointsValidityDays: number | null;
  if (rawDays === null || rawDays === undefined) {
    pointsValidityDays = null;
  } else if (typeof rawDays === 'number') {
    pointsValidityDays = Number.isFinite(rawDays) ? rawDays : null;
  } else if (typeof rawDays === 'string') {
    const trimmed = rawDays.trim();
    if (trimmed === '' || trimmed.toLowerCase() === 'null') {
      pointsValidityDays = null;
    } else {
      const n = Number(trimmed);
      pointsValidityDays = Number.isFinite(n) ? n : null;
    }
  } else {
    pointsValidityDays = null;
  }

  return { pointsValidityDays };
}

export function createGamificationConfigApi(api: GamificationConfigApiClient) {
  return {
    async fetchGamificationConfig(): Promise<GamificationConfigResponse> {
      const url = `/${GAMIFICATION_CONFIG_PATH}`;
      const raw = await api.get<unknown>(url);
      if (isEmptyGamificationResponse(raw)) {
        return emptyGamificationConfig();
      }
      return parseGamificationConfigRaw(raw);
    },

    async updateGamificationConfig(
      body: GamificationConfigUpdateRequest
    ): Promise<void> {
      const url = `/${GAMIFICATION_CONFIG_PATH}`;
      await api.post<unknown>(url, body);
    },
  };
}

export type GamificationConfigApi = ReturnType<
  typeof createGamificationConfigApi
>;
